// client/src/components/conversations/ActionQueuePanel.tsx

import { useState, useMemo } from 'react';
import { ConversationAction } from '../../types/api';
import {
  useRetryConversationActionMutation,
  useUpdateConversationActionMutation,
  useConfirmActionsMutation,
  useSkipActionsMutation,
} from '../../api/assistantApi';

interface ActionQueuePanelProps {
  actions: ConversationAction[];
  isExpanded?: boolean;
}

const ACTION_TYPE_LABELS: Record<string, string> = {
  create_task: 'Create Task',
  update_task: 'Update Task',
  create_event: 'Schedule Event',
  update_event: 'Update Event',
  create_contact: 'Add Contact',
  update_contact: 'Update Contact',
  create_pipeline_opportunity: 'Create Opportunity',
  update_pipeline_opportunity: 'Update Opportunity',
  send_internal_message: 'Send Message',
  send_email: 'Send Email',
  send_sms: 'Send SMS',
  add_memory: 'Remember',
  note: 'Note',
  custom: 'Custom Action',
};

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  pending_confirmation: 'bg-amber-100 text-amber-700',
  queued: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  skipped: 'bg-gray-100 text-gray-500',
};

const STATUS_ICONS: Record<string, string> = {
  pending: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  pending_confirmation: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  queued: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
  in_progress: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  completed: 'M5 13l4 4L19 7',
  failed: 'M6 18L18 6M6 6l12 12',
  skipped: 'M13 7l5 5m0 0l-5 5m5-5H6',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  pending_confirmation: 'Needs Confirmation',
  queued: 'Queued',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
};

function ConfidenceBadge({ confidence }: { confidence?: number }) {
  if (confidence === undefined) return null;

  const percentage = Math.round(confidence * 100);
  let colorClass = 'bg-gray-100 text-gray-600';

  if (percentage >= 90) {
    colorClass = 'bg-green-100 text-green-700';
  } else if (percentage >= 70) {
    colorClass = 'bg-yellow-100 text-yellow-700';
  } else {
    colorClass = 'bg-orange-100 text-orange-700';
  }

  return (
    <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${colorClass}`} title="AI Confidence">
      {percentage}%
    </span>
  );
}

export default function ActionQueuePanel({ actions, isExpanded = false }: ActionQueuePanelProps) {
  const [expanded, setExpanded] = useState(isExpanded);
  const [selectedActions, setSelectedActions] = useState<Set<string>>(new Set());

  const [retryAction, { isLoading: isRetrying }] = useRetryConversationActionMutation();
  const [skipSingleAction, { isLoading: isSkippingSingle }] = useUpdateConversationActionMutation();
  const [confirmActions, { isLoading: isConfirming }] = useConfirmActionsMutation();
  const [skipActions, { isLoading: isSkipping }] = useSkipActionsMutation();

  // Separate actions by confirmation status
  const { pendingConfirmation, otherActions } = useMemo(() => {
    const pending: ConversationAction[] = [];
    const other: ConversationAction[] = [];

    for (const action of actions) {
      if (action.actionStatus === 'pending_confirmation') {
        pending.push(action);
      } else {
        other.push(action);
      }
    }

    return { pendingConfirmation: pending, otherActions: other };
  }, [actions]);

  const handleRetry = async (actionId: string) => {
    try {
      await retryAction(actionId).unwrap();
    } catch (error) {
      console.error('Failed to retry action:', error);
    }
  };

  const handleSkipSingle = async (actionId: string) => {
    try {
      await skipSingleAction({
        id: actionId,
        data: { actionStatus: 'skipped' },
      }).unwrap();
    } catch (error) {
      console.error('Failed to skip action:', error);
    }
  };

  const handleToggleSelect = (actionId: string) => {
    setSelectedActions((prev) => {
      const next = new Set(prev);
      if (next.has(actionId)) {
        next.delete(actionId);
      } else {
        next.add(actionId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedActions.size === pendingConfirmation.length) {
      setSelectedActions(new Set());
    } else {
      setSelectedActions(new Set(pendingConfirmation.map((a) => a.id)));
    }
  };

  const handleConfirmSelected = async () => {
    if (selectedActions.size === 0) return;

    try {
      await confirmActions({ actionIds: Array.from(selectedActions) }).unwrap();
      setSelectedActions(new Set());
    } catch (error) {
      console.error('Failed to confirm actions:', error);
    }
  };

  const handleSkipSelected = async () => {
    if (selectedActions.size === 0) return;

    try {
      await skipActions({ actionIds: Array.from(selectedActions) }).unwrap();
      setSelectedActions(new Set());
    } catch (error) {
      console.error('Failed to skip actions:', error);
    }
  };

  const handleConfirmAll = async () => {
    if (pendingConfirmation.length === 0) return;

    try {
      await confirmActions({
        actionIds: pendingConfirmation.map((a) => a.id)
      }).unwrap();
      setSelectedActions(new Set());
    } catch (error) {
      console.error('Failed to confirm all actions:', error);
    }
  };

  if (actions.length === 0) {
    return null;
  }

  const pendingCount = actions.filter((a) =>
    a.actionStatus && ['pending', 'queued', 'in_progress'].includes(a.actionStatus)
  ).length;
  const awaitingConfirmCount = pendingConfirmation.length;
  const failedCount = actions.filter((a) => a.actionStatus === 'failed').length;
  const completedCount = actions.filter((a) => a.actionStatus === 'completed').length;

  const isAllSelected = selectedActions.size === pendingConfirmation.length && pendingConfirmation.length > 0;
  const isSomeSelected = selectedActions.size > 0;
  const isProcessing = isConfirming || isSkipping;

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <div className="text-left">
            <h3 className="font-medium text-gray-900">Action Queue</h3>
            <p className="text-xs text-gray-500">
              {actions.length} action{actions.length !== 1 ? 's' : ''}
              {awaitingConfirmCount > 0 && (
                <span className="text-amber-600 font-medium"> · {awaitingConfirmCount} awaiting confirmation</span>
              )}
              {pendingCount > 0 && ` · ${pendingCount} pending`}
              {failedCount > 0 && ` · ${failedCount} failed`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {awaitingConfirmCount > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full animate-pulse">
              {awaitingConfirmCount} to review
            </span>
          )}
          {completedCount > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
              {completedCount} done
            </span>
          )}
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {/* Pending Confirmation Section */}
          {pendingConfirmation.length > 0 && (
            <div className="bg-amber-50 border-b border-amber-100">
              <div className="px-4 py-2 flex items-center justify-between border-b border-amber-100">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium text-amber-800">
                    Pending Confirmation ({pendingConfirmation.length})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {isSomeSelected ? (
                    <>
                      <button
                        onClick={handleConfirmSelected}
                        disabled={isProcessing}
                        className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50"
                      >
                        {isConfirming ? 'Confirming...' : `Confirm (${selectedActions.size})`}
                      </button>
                      <button
                        onClick={handleSkipSelected}
                        disabled={isProcessing}
                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors disabled:opacity-50"
                      >
                        {isSkipping ? 'Skipping...' : `Skip (${selectedActions.size})`}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleConfirmAll}
                      disabled={isProcessing}
                      className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-md transition-colors disabled:opacity-50"
                    >
                      Confirm All
                    </button>
                  )}
                </div>
              </div>

              <div className="divide-y divide-amber-100 max-h-48 overflow-y-auto">
                {pendingConfirmation.map((action) => (
                  <div key={action.id} className="px-4 py-3 hover:bg-amber-100/50">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedActions.has(action.id)}
                        onChange={() => handleToggleSelect(action.id)}
                        className="mt-1 w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm text-gray-900">
                            {ACTION_TYPE_LABELS[action.actionType || 'unknown'] || action.actionType || 'Unknown'}
                          </p>
                          <ConfidenceBadge confidence={action.confidence} />
                          {action.suggestedBy && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                              {action.suggestedBy}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5">{action.summary}</p>
                        {action.explanation && (
                          <p className="text-xs text-gray-500 mt-1 italic">
                            {action.explanation}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Other Actions Section */}
          {otherActions.length > 0 && (
            <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {otherActions.map((action) => (
                <div key={action.id} className="px-4 py-3 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className={`p-1.5 rounded ${STATUS_STYLES[action.actionStatus || 'pending']}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d={STATUS_ICONS[action.actionStatus || 'pending']}
                          />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm text-gray-900">
                            {ACTION_TYPE_LABELS[action.actionType || 'unknown'] || action.actionType || 'Unknown'}
                          </p>
                          <ConfidenceBadge confidence={action.confidence} />
                        </div>
                        <p className="text-xs text-gray-500 truncate">{action.summary}</p>
                        {action.explanation && (
                          <p className="text-xs text-gray-400 mt-0.5 italic truncate">
                            {action.explanation}
                          </p>
                        )}
                        {action.errorMessage && (
                          <p className="text-xs text-red-600 mt-1">{action.errorMessage}</p>
                        )}
                        {action.targetId && (
                          <p className="text-xs text-gray-400 mt-1">
                            Created: {action.targetType} #{action.targetId.slice(0, 8)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          STATUS_STYLES[action.actionStatus || 'pending']
                        }`}
                      >
                        {STATUS_LABELS[action.actionStatus || 'pending'] || action.actionStatus || 'pending'}
                      </span>
                      {action.actionStatus === 'failed' && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleRetry(action.id)}
                            disabled={isRetrying}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                            title="Retry"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleSkipSingle(action.id)}
                            disabled={isSkippingSingle}
                            className="p-1 text-gray-500 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                            title="Skip"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state if no actions in either section */}
          {pendingConfirmation.length === 0 && otherActions.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-500">
              <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-sm">No actions in queue</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
